import { useEffect, useRef } from 'react'

/**
 * Polls the deployed build stamp (public/version.json, overwritten per build by
 * scripts/write-version.js) and calls back once, the first time it differs from
 * the value read when this tab loaded -- unless that newly-seen build was
 * published with `notify: false` (SHOW_UPDATE_TOAST=false at build time), in
 * which case it is skipped silently and a later build can still notify.
 *
 * A tab keeps the bundle it downloaded, so a published update only reaches the
 * user on a reload; this is what lets the app ask for one.
 */

type VersionFile = { version?: string; commit?: string; builtAt?: string; notify?: boolean }
type FetchedVersion = { version: string; notify: boolean }

const VERSION_URL = `${process.env.PUBLIC_URL ?? ''}/version.json`
const DEFAULT_INTERVAL_MS = 3 * 60 * 1000

async function fetchVersion(): Promise<FetchedVersion | null> {
  try {
    // The query string and no-store both matter: without them a tab can keep
    // re-reading the copy it cached when it loaded and never see a new build.
    const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, { cache: 'no-store' })
    if (!response.ok) return null
    const data = (await response.json()) as VersionFile
    const version = data.version ?? null
    if (version === null) return null
    // A file predating this field defaults to notifying, same as before it existed.
    return { version, notify: data.notify ?? true }
  } catch {
    // Offline, or the file is missing (a dev server without public/). Silent:
    // the next poll retries, and a failed read must never look like an update.
    return null
  }
}

export function useVersionCheck(
  onNewVersion: () => void,
  intervalMs: number = DEFAULT_INTERVAL_MS,
): void {
  const loadedVersion = useRef<string | null>(null)
  const notified = useRef(false)
  const callback = useRef(onNewVersion)
  callback.current = onNewVersion

  useEffect(() => {
    let cancelled = false

    const check = async (): Promise<void> => {
      const current = await fetchVersion()
      if (cancelled || !current) return

      if (loadedVersion.current === null) {
        loadedVersion.current = current.version
        return
      }

      if (current.version !== loadedVersion.current && current.notify && !notified.current) {
        notified.current = true
        callback.current()
      }
    }

    check()
    const intervalId = window.setInterval(check, intervalMs)
    const onFocus = (): void => {
      check()
    }
    window.addEventListener('focus', onFocus)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
      window.removeEventListener('focus', onFocus)
    }
  }, [intervalMs])
}
